"use client"

import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Upload } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useTranslations } from "@/components/i18n/use-translations";

const SAMPLE_HEADERS = `Received: from mail.example-bank-secure.top (unknown [203.0.113.45])
\tby mx.google.com with ESMTPS id a1si123456
\tfor <victim@example.com>; Tue, 30 Sep 2026 10:15:42 -0700 (PDT)
Received: from [10.0.0.12] (helo=localhost)
\tby mail.example-bank-secure.top with esmtpa; Tue, 30 Sep 2026 17:15:30 +0000
Authentication-Results: mx.google.com;
       spf=softfail smtp.mailfrom=billing@example-bank.com;
       dkim=none;
       dmarc=fail (p=REJECT) header.from=example-bank.com
Return-Path: <bounce@example-bank-secure.top>
From: "Example Bank" <billing@example-bank.com>
Reply-To: support@payout-helpdesk.xyz
To: victim@example.com
Subject: Action required: verify your account
Date: Tue, 30 Sep 2026 17:15:29 +0000
Message-ID: <abc123@example-bank-secure.top>`;

const SUMMARY_FIELDS = ["From", "Reply-To", "Return-Path", "To", "Subject", "Date", "Message-ID", "X-Originating-IP", "X-Mailer"];

const parseHeaders = (raw: string) => {
    const unfolded = raw.replace(/\r\n/g, "\n").replace(/\n[ \t]+/g, " ");
    const headers: { name: string; value: string }[] = [];
    unfolded.split("\n").forEach((line) => {
        const match = line.match(/^([!-9;-~]+):\s*(.*)$/);
        if (match) {
            headers.push({ name: match[1], value: match[2].trim() });
        }
    });
    return headers;
};

const domainOf = (value: string | undefined) => {
    const match = value?.match(/@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/);
    return match ? match[1].toLowerCase() : null;
};

const analyze = (raw: string) => {
    const headers = parseHeaders(raw);
    if (!headers.length) {
        return null;
    }
    const get = (name: string) => headers.find((header) => header.name.toLowerCase() === name.toLowerCase())?.value;

    const authRaw = headers
        .filter((header) => /^(authentication-results|arc-authentication-results|received-spf)$/i.test(header.name))
        .map((header) => header.value)
        .join(" ");
    const auth = (["spf", "dkim", "dmarc"] as const).map((mechanism) => {
        const match = authRaw.match(new RegExp(`\\b${mechanism}=([a-z]+)`, "i"));
        const spfHeader = mechanism === "spf" ? get("Received-SPF")?.match(/^([a-z]+)/i)?.[1] : undefined;
        return { mechanism, result: (match?.[1] ?? spfHeader ?? "").toLowerCase() };
    });

    // Received headers are prepended, so reverse to get origin first
    const hops = headers
        .filter((header) => header.name.toLowerCase() === "received")
        .reverse()
        .map((header) => {
            const [route, dateText] = header.value.split(/;(?=[^;]*$)/);
            const date = dateText ? new Date(dateText.trim().replace(/\s*\([^)]*\)\s*$/, "")) : null;
            return {
                from: route.match(/\bfrom\s+(.+?)(?=\s+by\s|$)/i)?.[1] ?? "",
                by: route.match(/\bby\s+(\S+)/i)?.[1] ?? "",
                ips: Array.from(new Set(route.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b|\b[0-9a-f]{1,4}(?::[0-9a-f]{0,4}){2,7}\b/gi) ?? [])),
                date: date && !Number.isNaN(date.getTime()) ? date : null,
            };
        });

    const fromDomain = domainOf(get("From"));
    const replyToDomain = domainOf(get("Reply-To"));
    const returnPathDomain = domainOf(get("Return-Path"));

    return {
        summary: SUMMARY_FIELDS.map((name) => ({ name, value: get(name) })).filter((field) => field.value),
        auth,
        hops,
        warnings: {
            replyTo: Boolean(fromDomain && replyToDomain && fromDomain !== replyToDomain),
            returnPath: Boolean(fromDomain && returnPathDomain && fromDomain !== returnPathDomain),
            authFail: auth.some((item) => ["fail", "softfail", "none", "permerror", "temperror"].includes(item.result)),
        },
        fromDomain,
        replyToDomain,
        returnPathDomain,
    };
};

const AUTH_TONES: Record<string, string> = {
    pass: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    neutral: "border-slate-600 bg-slate-800/60 text-gray-300",
    "": "border-slate-700 bg-slate-900/60 text-gray-500",
};

export function EmailHeaderAnalyzer() {
    const [raw, setRaw] = useState("");
    const [fileName, setFileName] = useState<string | null>(null);
    const { messages } = useTranslations();
    const t = messages.tools.emailHeaderAnalyzer;

    const result = useMemo(() => analyze(raw), [raw]);

    // Read the .eml locally and keep only the header block (everything before the first blank line)
    const handleFile = async (file?: File) => {
        if (!file) {
            return;
        }
        const text = (await file.text()).replace(/\r\n/g, "\n");
        const blankLine = text.search(/\n[ \t]*\n/);
        setRaw(blankLine === -1 ? text : text.slice(0, blankLine));
        setFileName(file.name);
    };

    const warnings = result
        ? [
              ...(result.warnings.replyTo ? [t.warningReplyTo.replace("{from}", result.fromDomain ?? "").replace("{other}", result.replyToDomain ?? "")] : []),
              ...(result.warnings.returnPath
                  ? [t.warningReturnPath.replace("{from}", result.fromDomain ?? "").replace("{other}", result.returnPathDomain ?? "")]
                  : []),
              ...(result.warnings.authFail ? [t.warningAuthFail] : []),
          ]
        : [];

    return (
        <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-8">
            <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{t.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{t.title}</h1>
                    <p className="text-sm text-gray-400">{t.description}</p>
                </div>
                <Textarea
                    value={raw}
                    onChange={(event) => {
                        setRaw(event.target.value);
                        setFileName(null);
                    }}
                    placeholder={t.placeholder}
                    aria-label={t.placeholder}
                    spellCheck={false}
                    className="min-h-[220px] border border-slate-800 bg-transparent font-mono text-xs text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                />
                <div className="flex flex-wrap items-center gap-3">
                    <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-sm font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35">
                        <Input
                            type="file"
                            accept=".eml,.txt,message/rfc822"
                            className="hidden"
                            onChange={(event) => {
                                handleFile(event.target.files?.[0] ?? undefined);
                                event.target.value = "";
                            }}
                        />
                        <Upload className="h-4 w-4" aria-hidden="true" />
                        <span>{t.uploadEml}</span>
                    </label>
                    <button
                        type="button"
                        onClick={() => {
                            setRaw(SAMPLE_HEADERS);
                            setFileName(null);
                        }}
                        className="rounded-full border border-slate-700 px-3 py-1 text-xs text-gray-300 transition-colors hover:border-cyan-500/60 hover:text-cyan-200"
                    >
                        {t.useSample}
                    </button>
                    {fileName && <span className="truncate text-xs text-gray-400">{fileName}</span>}
                </div>
                <p className="text-xs text-gray-500">{t.uploadNote}</p>
            </div>

            {raw.trim() && !result && <p className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{t.empty}</p>}

            {result && (
                <div className="space-y-4">
                    <div className="space-y-2 rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
                        <h3 className="text-sm font-semibold text-white">{t.warnings}</h3>
                        {warnings.length ? (
                            <ul className="space-y-2">
                                {warnings.map((warning) => (
                                    <li key={warning} className="flex items-start gap-2 text-sm text-amber-100">
                                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                                        <span>{warning}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="flex items-center gap-2 text-sm text-emerald-200">
                                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                                {t.noWarnings}
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
                        <div className="space-y-2 rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
                            <h3 className="text-sm font-semibold text-white">{t.summary}</h3>
                            <dl className="space-y-2 text-sm">
                                {result.summary.map((field) => (
                                    <div key={field.name} className="grid grid-cols-[110px_1fr] gap-2">
                                        <dt className="font-mono text-xs text-cyan-200">{field.name}</dt>
                                        <dd className="break-all text-gray-200">{field.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                        <div className="space-y-2 rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
                            <h3 className="text-sm font-semibold text-white">{t.authentication}</h3>
                            <ul className="space-y-2">
                                {result.auth.map((item) => (
                                    <li key={item.mechanism} className="flex items-center justify-between text-sm">
                                        <span className="font-mono uppercase text-gray-300">{item.mechanism}</span>
                                        <span
                                            className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                                                AUTH_TONES[item.result] ?? "border-red-500/40 bg-red-500/10 text-red-300"
                                            }`}
                                        >
                                            {item.result || "-"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="space-y-2 rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
                        <h3 className="text-sm font-semibold text-white">
                            {t.hops} ({result.hops.length})
                        </h3>
                        <ol className="space-y-2">
                            {result.hops.map((hop, index) => {
                                const previous = result.hops[index - 1]?.date;
                                const delay = hop.date && previous ? Math.round((hop.date.getTime() - previous.getTime()) / 1000) : null;
                                return (
                                    <li key={index} className="rounded-lg border border-slate-800/70 bg-slate-900/60 px-3 py-2 text-sm">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <span className="font-semibold text-white">#{index + 1}</span>
                                            <span className="text-xs text-gray-400">
                                                {hop.date ? hop.date.toISOString() : "-"}
                                                {delay !== null && (
                                                    <span className={`ml-2 ${delay < 0 || delay > 300 ? "text-amber-300" : "text-gray-500"}`}>
                                                        ({t.hopDelay}: {delay}s)
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <p className="break-all text-gray-300">
                                            <span className="text-gray-500">{t.from}:</span> {hop.from || "-"}
                                        </p>
                                        <p className="break-all text-gray-300">
                                            <span className="text-gray-500">{t.by}:</span> {hop.by || "-"}
                                        </p>
                                        {hop.ips.length > 0 && <p className="break-all font-mono text-xs text-cyan-200">{hop.ips.join(", ")}</p>}
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
}
